import { useEffect, useState } from 'react';
import { InvoicingWorkspace } from '@/features/invoicing';
import { SalaryWorkspace } from '@/features/salary';
import { ExtrasWorkspace } from '@/features/extras';
import { type Language } from '@/types';

interface EconomyWorkspaceProps {
  language: Language;
}

type EconomyTab = 'fakturering' | 'loner' | 'extra';

const TAB_LABELS: Record<EconomyTab, { sv: string; en: string }> = {
  fakturering: { sv: 'Fakturering', en: 'Invoicing' },
  loner: { sv: 'Löner', en: 'Payroll' },
  extra: { sv: 'Extra', en: 'Extras' },
};

const VALID_TABS: EconomyTab[] = ['fakturering', 'loner', 'extra'];

function readTabFromHash(): EconomyTab {
  const hash = window.location.hash.replace('#/', '').replace('#', '');
  const segments = hash.split('/').filter(Boolean);
  // segments[0] === 'economy', segments[1] is the tab (strip any ?query a child appended)
  const sub = (segments[1]?.split('?')[0]) as EconomyTab | undefined;
  return sub && VALID_TABS.includes(sub) ? sub : 'fakturering';
}

export function EconomyWorkspace({ language }: EconomyWorkspaceProps) {
  const [activeTab, setActiveTab] = useState<EconomyTab>(readTabFromHash);

  // Sync tab when the hash changes externally (e.g. back/forward)
  useEffect(() => {
    const handleHashChange = () => {
      const hash = window.location.hash.replace('#/', '').replace('#', '');
      const segments = hash.split('/').filter(Boolean);
      if (segments[0] === 'economy') {
        const sub = (segments[1]?.split('?')[0]) as EconomyTab | undefined;
        const next = sub && VALID_TABS.includes(sub) ? sub : 'fakturering';
        setActiveTab(next);
      }
    };

    window.addEventListener('hashchange', handleHashChange);
    return () => window.removeEventListener('hashchange', handleHashChange);
  }, []);

  const handleTabChange = (tab: EconomyTab) => {
    setActiveTab(tab);
    window.location.hash = `/economy/${tab}`;
  };

  return (
    <div className="cmd-canvas -m-4 -mt-16 p-4 pt-16 md:-m-8 md:p-8 min-h-full">
      {/* Tab bar — mono uppercase with bronze active underline (Portfölj toggle pattern) */}
      <div className="flex items-center gap-5 mb-4 border-b border-[#073331]/10">
        {VALID_TABS.map((tab) => {
          const active = activeTab === tab;
          return (
            <button
              key={tab}
              type="button"
              onClick={() => handleTabChange(tab)}
              className="cmd-label pb-2 -mb-px transition-colors"
              style={{
                color: active ? '#8A6A47' : '#607C6E',
                borderBottom: active ? '1.5px solid #8A6A47' : '1.5px solid transparent',
              }}
            >
              {TAB_LABELS[tab][language]}
            </button>
          );
        })}
      </div>

      {activeTab === 'fakturering' && <InvoicingWorkspace language={language} />}
      {activeTab === 'loner' && <SalaryWorkspace language={language} />}
      {activeTab === 'extra' && <ExtrasWorkspace language={language} />}
    </div>
  );
}
